import {
  CHART_NAME_MAX_LENGTH,
  DASHBOARD_WORKSPACE_NAME_MAX_LENGTH,
  ROLE_NAME_MAX_LENGTH,
  USER_GROUP_NAME_MAX_LENGTH,
} from '../../config/constants';
import {
  AnalyticsErrorContext,
  IAnalyticsErrorArgument,
} from '../../utils/analytics/events/error/types';
import { countZodErrors } from '../Builder/utils/errorUtil';
import { ZodFormattedError } from 'zod';

export type IInfoModalEntity =
  | 'duplicateDashboard'
  | 'workspace'
  | 'createUserGroup'
  | 'editUserGroup'
  | 'editRole'
  | 'createRole'
  | 'duplicateDashboardReport';

type IAnalyticsErrorArgumentProducer = (
  errors: ZodFormattedError<unknown> | null,
) => IAnalyticsErrorArgument;

interface IInfoModalEntityInfo {
  header: string;
  targetElement: string;
  nameLabel: string;
  descriptionLabel: string;
  nameLimit: number;
  analyticsErrorProducer: IAnalyticsErrorArgumentProducer;
}

export const editInfoModalLookup: Record<
  IInfoModalEntity,
  IInfoModalEntityInfo
> = {
  workspace: {
    header: 'Edit workspace info',
    targetElement: 'Workspace',
    nameLabel: 'Workspace name',
    descriptionLabel: 'Workspace description',
    nameLimit: DASHBOARD_WORKSPACE_NAME_MAX_LENGTH,
    analyticsErrorProducer: (errors) => ({
      message: `Failed to update workspace. ${countZodErrors(errors)} errors found.`,
      context: AnalyticsErrorContext.SaveWorkspace,
    }),
  },
  duplicateDashboard: {
    header: 'Duplicate dashboard',
    targetElement: 'Dashboard',
    nameLabel: 'New dashboard name',
    descriptionLabel: 'Dashboard description',
    nameLimit: DASHBOARD_WORKSPACE_NAME_MAX_LENGTH,
    analyticsErrorProducer: (errors) => ({
      message: `Failed to duplicate dashboard. ${countZodErrors(errors)} errors found.`,
      context: AnalyticsErrorContext.DuplicateDashboard,
    }),
  },
  createUserGroup: {
    header: 'Create Group',
    targetElement: 'Group',
    nameLabel: 'Group name',
    descriptionLabel: 'Group description',
    nameLimit: USER_GROUP_NAME_MAX_LENGTH,
    analyticsErrorProducer: (errors) => ({
      message: `Failed to create group. ${countZodErrors(errors)} errors found.`,
      context: AnalyticsErrorContext.SaveGroup,
    }),
  },
  editUserGroup: {
    header: 'Edit Group',
    targetElement: 'Group',
    nameLabel: 'Group name',
    descriptionLabel: 'Group description',
    nameLimit: USER_GROUP_NAME_MAX_LENGTH,
    analyticsErrorProducer: (errors) => ({
      message: `Failed to update group. ${countZodErrors(errors)} errors found.`,
      context: AnalyticsErrorContext.SaveGroup,
    }),
  },
  editRole: {
    header: 'Edit Role',
    targetElement: 'Role',
    nameLabel: 'Role name',
    descriptionLabel: 'Role description',
    nameLimit: ROLE_NAME_MAX_LENGTH,
    analyticsErrorProducer: (errors) => ({
      message: `Failed to update role. ${countZodErrors(errors)} errors found.`,
      context: AnalyticsErrorContext.SaveRole,
    }),
  },
  createRole: {
    header: 'Create Role',
    targetElement: 'Role',
    nameLabel: 'Role name',
    descriptionLabel: 'Role description',
    nameLimit: ROLE_NAME_MAX_LENGTH,
    analyticsErrorProducer: (errors) => ({
      message: `Failed to create role. ${countZodErrors(errors)} errors found.`,
      context: AnalyticsErrorContext.SaveRole,
    }),
  },
  duplicateDashboardReport: {
    header: 'Duplicate Report',
    targetElement: 'Report',
    nameLabel: 'Report name',
    descriptionLabel: 'Report description',
    nameLimit: CHART_NAME_MAX_LENGTH,
    analyticsErrorProducer: (errors) => ({
      message: `Failed to duplicate duplicate dashboard report. ${countZodErrors(errors)} errors found.`,
      context: AnalyticsErrorContext.DuplicateDashboardReport,
    }),
  },
};
